import { useEffect, useId, useRef, useState } from "react";
import { useBrand } from "../branding/BrandContext";
import { PRODUCT_NAME } from "../branding/config";
import { useI18n } from "../i18n";
import { monogram } from "../lib/format";
import { IconExternal, IconHelp, IconMail } from "./icons";
import { CopyButton } from "./ui";

/** Operator logo, or its name with a monogram when there is no logo or the image fails to load. */
export function BrandMark() {
  const { brand } = useBrand();
  const [failed, setFailed] = useState<string | null>(null);
  const logo = brand.logoUrl && failed !== brand.logoUrl ? brand.logoUrl : null;
  return (
    <span className="brand">
      {logo ? (
        <img
          className="brand-logo"
          src={logo}
          alt={brand.name}
          height={28}
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed(logo)}
          onLoad={(e) => {
            // A 1×1 tracking pixel or an empty SVG is as good as no logo.
            if (e.currentTarget.naturalWidth < 2) setFailed(logo);
          }}
        />
      ) : (
        <>
          <span className="brand-monogram" aria-hidden="true">
            {monogram(brand.name)}
          </span>
          <span className="brand-name">{brand.name}</span>
        </>
      )}
    </span>
  );
}

/**
 * "Need help?" — a small disclosure with the operator's support links and, on a payment page, the
 * payment ID to quote. Renders nothing when the operator configured no support contact.
 */
export function HelpMenu({ invoiceId }: { invoiceId?: string }) {
  const { brand } = useBrand();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onPointer = (e: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  const email = brand.supportEmail;
  const site = brand.supportUrl;
  if (!email && !site) return null;
  const mailto = email
    ? `mailto:${email}${invoiceId ? `?subject=${encodeURIComponent(t("help.subject", { id: invoiceId }))}` : ""}`
    : null;

  return (
    <div className="help" ref={wrap}>
      <button
        ref={button}
        type="button"
        className="help-btn"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
      >
        <IconHelp size={16} />
        <span>{t("help.button")}</span>
      </button>
      <div id={panelId} className="help-panel" role="region" aria-label={t("help.title")} hidden={!open}>
        <p className="help-title">{t("help.title")}</p>
        <ul className="help-links" role="list">
          {mailto ? (
            <li>
              <a className="help-link" href={mailto}>
                <IconMail size={17} />
                <span className="help-link-text">
                  <span>{t("help.email")}</span>
                  <span className="muted small">{email}</span>
                </span>
              </a>
            </li>
          ) : null}
          {site ? (
            <li>
              <a className="help-link" href={site} target="_blank" rel="noopener noreferrer">
                <IconExternal size={17} />
                <span className="help-link-text">
                  <span>{t("help.site")}</span>
                  <span className="muted small">{hostOf(site)}</span>
                </span>
              </a>
            </li>
          ) : null}
        </ul>
        {invoiceId ? (
          <div className="help-ref">
            <span className="field-label">{t("help.reference")}</span>
            <span className="help-ref-row">
              <span className="mono small">{invoiceId}</span>
              <CopyButton value={invoiceId} label={t("common.invoice")} compact />
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Footer: operator line, legal links, help and the optional "Powered by". */
export function SiteFooter({ invoiceId }: { invoiceId?: string }) {
  const { brand } = useBrand();
  const { t } = useI18n();
  const links = [
    brand.termsUrl ? { href: brand.termsUrl, label: t("footer.terms") } : null,
    brand.privacyUrl ? { href: brand.privacyUrl, label: t("footer.privacy") } : null,
  ].filter((l): l is { href: string; label: string } => l !== null);
  const hasHelp = Boolean(brand.supportEmail || brand.supportUrl);
  // "Powered by" credits the software; it would only repeat the header when the brand is unchanged.
  const powered = brand.showPoweredBy && brand.name !== PRODUCT_NAME;
  if (!brand.footerText && links.length === 0 && !hasHelp && !powered) return null;
  return (
    <footer className="site-footer">
      <div className="site-footer-row">
        <HelpMenu invoiceId={invoiceId} />
        {links.length ? (
          <nav className="footer-links" aria-label={`${t("footer.terms")} · ${t("footer.privacy")}`}>
            {links.map((l) => (
              <a key={l.href} className="footer-link" href={l.href} target="_blank" rel="noopener noreferrer">
                {l.label}
              </a>
            ))}
          </nav>
        ) : null}
      </div>
      {brand.footerText ? <p className="footer-text">{brand.footerText}</p> : null}
      {powered ? <p className="footer-powered">{t("footer.poweredBy", { name: PRODUCT_NAME })}</p> : null}
    </footer>
  );
}
